import PageHeader from '../../components/PageHeader';
import { useGetApproverWorkflows } from '../../services/workflow.services';
import moment from 'moment';
import styled from 'styled-components';
import Table from '../../components/Table';
import { TbExternalLink } from 'react-icons/tb';
import { jurisdictionService } from '../../services/jurisdiction.service';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Tabs, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createStyles } from '@mantine/emotion';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useFeatureFlag, FEATURE_FLAG_NAMES } from '../../hooks/useFeatureFlag';
import { SortButton } from './components/SortButton';
import { REPORT_TYPES } from '../../consts/reportTypes';

const useStyles = createStyles(theme => ({
  borderedBox: {
    border: `1px solid ${theme.colors.gray[1]}`,
    padding: theme.spacing.md,
    borderRadius: '4px',
    background: '#fff',
    marginTop: '12px',
    overflow: 'hidden',
    height: '100%'
  }
}));

interface ApproverWorkflow {
  workflow_id: string;
  workflow_name: string;
  workflow_type: string;
  period_id: string;
  period: string;
  year: number | string;
  date_sent?: string;
  date_approved?: string;
  is_aif_approve_required?: boolean;
  is_aifm_approve_required?: boolean;
  is_aifm_included?: boolean;
  is_aifm_approved?: boolean;
  status?: { is_approved: boolean }[];
}

export function ProjectListApprovers() {
  const { data: approverWorkflows, isLoading } = useGetApproverWorkflows() as {
    data?: ApproverWorkflow[];
    isLoading: boolean;
  };
  const workflows = approverWorkflows || [];

  // Keep reference to avoid unused import warning; feature flag not used yet for Form PF
  useFeatureFlag(FEATURE_FLAG_NAMES.FORM_PF);

  const uniqueWorkflowTypes = useMemo(() => {
    return [...new Set(workflows.map(item => item.workflow_type))];
  }, [workflows]);

  const navigate = useNavigate();
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({
    key: '',
    direction: ''
  });

  useEffect(() => {
    if (uniqueWorkflowTypes.length > 0) {
      setActiveTab(uniqueWorkflowTypes[0]);
    }
  }, [uniqueWorkflowTypes]);

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab(value || '');
  }, []);

  const onOpenReport = useCallback(
    async (record: ApproverWorkflow) => {
      const { workflow_id: workflowId, period_id: periodId, workflow_type: workflowType } = record;

      if (workflowType === 'Form PF') {
        navigate(`/report/pf/us/${workflowId}/${periodId}`);
        return;
      }

      try {
        const res: any = await jurisdictionService.getJurisdictionsApprover(periodId);

        const el = res?.[0];
        if (!el) {
          notifications.show({
            color: 'red',
            title: 'No report jurisdiction is available for this review.',
            position: 'bottom-center',
            autoClose: 4000
          });
          return;
        }

        const natCode = el?.reporting_member_state;
        const isFCA = natCode === 'UK' || natCode === 'uk';
        const isAifmOnly = record.is_aifm_included && !record.status?.length;
        const groupedQuery = isAifmOnly ? '?type=AIF001' : '';

        if (isFCA) navigate(`/report/fca/uk/${workflowId}/${periodId}${groupedQuery}`);
        else
          navigate(
            `/report/esma/${natCode?.toLowerCase()}/${workflowId}/${periodId}${groupedQuery}`
          );
      } catch (e) {
        notifications.show({
          color: 'red',
          title: 'Unable to open this report. Please try again or contact support.',
          position: 'bottom-center',
          autoClose: 4000
        });
      }
    },
    [navigate]
  );

  const formatDate = (value: string) => moment(value).format('YYYY, MMMM D, h:mm A');

  const getAifStatus = (record: ApproverWorkflow) => {
    if (!record.is_aif_approve_required) {
      return { text: 'N/A', approved: false };
    }

    const approvedFundsCount = record.status?.filter(item => item.is_approved).length || 0;
    const totalFundsCount = record.status?.length || 0;
    const isApproved = totalFundsCount > 0 && approvedFundsCount === totalFundsCount;

    if (isApproved) return { text: 'Approved', approved: true };
    if (approvedFundsCount === 0) return { text: 'To Approve', approved: false };
    return { text: `${approvedFundsCount}/${totalFundsCount} Funds Approved`, approved: false };
  };

  const getAifmStatus = (record: ApproverWorkflow) => {
    if (!record.is_aifm_approve_required) {
      return { text: 'N/A', approved: false };
    }

    return {
      text: record.is_aifm_approved ? 'Approved' : 'To Approve',
      approved: !!record.is_aifm_approved
    };
  };

  const sortHeader = useCallback(
    (title: string, columnName: string) => () => (
      <Flex align="center" gap="4px">
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          {title}
        </Text>
        <SortButton columnName={columnName} setSortConfig={setSortConfig} sortConfig={sortConfig} />
      </Flex>
    ),
    [sortConfig]
  );

  const createColumns = useCallback(
    ({ aifTitle, aifmTitle }: { aifTitle: string; aifmTitle: string }) => [
      {
        title: 'Fund Manager',
        key: 'workflow_name',
        renderHeadCell: sortHeader('Fund Manager', 'workflow_name')
      },
      {
        title: 'Reporting Period',
        key: 'sort_period',
        renderHeadCell: sortHeader('Reporting Period', 'sort_period'),
        render: (record: ApproverWorkflow) => `${record.period}, ${record.year}`
      },
      {
        title: 'Sent for Reviewer',
        key: 'date_sent',
        renderHeadCell: sortHeader('Sent for Reviewer', 'date_sent'),
        render: (record: ApproverWorkflow) => (record.date_sent ? formatDate(record.date_sent) : '')
      },
      {
        title: 'Approval Date',
        key: 'date_approved',
        renderHeadCell: sortHeader('Approval Date', 'date_approved'),
        render: (record: ApproverWorkflow) =>
          record.is_aif_approve_required || record.is_aifm_approve_required
            ? record?.date_approved
              ? formatDate(record.date_approved)
              : '-'
            : 'N/A'
      },
      {
        title: aifTitle,
        key: 'sort_aif_status',
        renderHeadCell: sortHeader(aifTitle, 'sort_aif_status'),
        render: (record: ApproverWorkflow) => {
          const aifStatus = getAifStatus(record);
          return <StatusStyle approved={aifStatus.approved}>{aifStatus.text}</StatusStyle>;
        }
      },
      {
        title: aifmTitle,
        key: 'sort_aifm_status',
        renderHeadCell: sortHeader(aifmTitle, 'sort_aifm_status'),
        render: (record: ApproverWorkflow) => {
          const aifmStatus = getAifmStatus(record);
          return <StatusStyle approved={aifmStatus.approved}>{aifmStatus.text}</StatusStyle>;
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (record: ApproverWorkflow) => (
          <Flex gap="16px">
            <ViewReportStyle onClick={() => onOpenReport(record)}>
              View Report
              <TbExternalLink fontSize="16px" />
            </ViewReportStyle>
            {record.workflow_type !== REPORT_TYPES.CONVERSION && (
              <ViewReportStyle
                onClick={() => navigate(`/output-tables/${record.workflow_id}/${record.period_id}`)}
              >
                View Output Data
                <TbExternalLink fontSize="16px" />
              </ViewReportStyle>
            )}
          </Flex>
        )
      }
    ],
    [navigate, onOpenReport, sortHeader]
  );

  const defaultColumns = useMemo(
    () =>
      createColumns({
        aifTitle: 'AIF Status',
        aifmTitle: 'AIFM Status'
      }),
    [createColumns]
  );

  const formPfColumns = useMemo(
    () =>
      createColumns({
        aifTitle: 'Fund Status',
        aifmTitle: 'Managers Status'
      }),
    [createColumns]
  );

  const tableData = useMemo(() => {
    const enriched = workflows
      .filter(item => item.workflow_type === activeTab)
      .map(item => ({
        ...item,
        // Sort-only fields keyed to match the sortable columns; the columns
        // render their own display, so these values are never shown.
        sort_period: `${String(item.year).padStart(4, '0')}-${item.period}`,
        sort_aif_status: getAifStatus(item).text,
        sort_aifm_status: getAifmStatus(item).text
      }));

    const { key, direction } = sortConfig;
    // No active column sort → default to Reporting Period (newest first) in the
    // background, without marking the period column's sort button as active.
    if (!key) return enriched.sort((a, b) => b.sort_period.localeCompare(a.sort_period));

    // SortButton encodes ascending as `key` and descending as `-key`.
    const isAsc = direction === key;
    return enriched.sort((a, b) => {
      const aValue = (a as Record<string, any>)[key];
      const bValue = (b as Record<string, any>)[key];
      if (aValue < bValue) return isAsc ? -1 : 1;
      if (aValue > bValue) return isAsc ? 1 : -1;
      return 0;
    });
  }, [workflows, activeTab, sortConfig]);

  return (
    <Box
      h="calc(100vh - 68px)"
      bg="gray.0"
      pt="16"
      px="16"
      pb="3"
      // @ts-ignore
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <PageHeader title="Projects" />

      <Box className={classes.borderedBox}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List mb="16px" w="fit-content">
            {uniqueWorkflowTypes.map(tab => (
              <Tabs.Tab value={tab} key={tab}>
                {tab}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {uniqueWorkflowTypes.map(tab => (
            <Tabs.Panel value={tab} key={tab}>
              {/* @ts-ignore */}
              <Table
                hasIndex={false}
                isLoading={isLoading}
                columns={tab === 'Form PF' ? formPfColumns : defaultColumns}
                data={tableData}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </Box>
    </Box>
  );
}

const StatusStyle = styled.div<{ approved: boolean }>`
  color: #868e96;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ approved }) => (!approved ? '#868e96' : '#40C057')};
`;

const ViewReportStyle = styled.div`
  color: #228be6;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
